import { useMemo, useState, type ReactNode } from 'react';
import { DataError, useList } from '@hana/data';
import { findCarrier } from '@hana/domain';
import { formatDate, useT } from '@hana/i18n';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Chip, Input, Modal, StateView, Table, useToast, type Column, type ScreenState } from '@hana/ui';
import { IconCheck, IconChevronDown, IconClipboard, IconDots, IconHistory, IconLock, IconPencil, IconPlus, IconSearch, IconSend, IconTrash, IconX } from '@tabler/icons-react';
import { useP2PAccess } from './access';
import { usd } from './format';
import { MARGIN_ORDER, MARGIN_ORDER_LABEL, convertMoney, latestRoe, matchRow } from './setup';
import type { Country, CustomerSurcharge, LocalCharge, MarginApproval, MarginRule, RoeRow, TariffService, TransshipHub } from './types';
import { SetupDrawer, SetupHistory, type SetupDrawerState, type SetupForm } from './SetupDrawer';
import './PriceSetupScreen.i18n';
import s from './setup.module.css';

type Tab = 'lcc' | 'hub' | 'tariff' | 'cus' | 'roe' | 'ctry' | 'margin';
const TABS: readonly { key: Tab; label: string }[] = [
  { key: 'lcc', label: 'ps.tLcc' },
  { key: 'hub', label: 'ps.tHub' },
  { key: 'tariff', label: 'ps.tTariff' },
  { key: 'cus', label: 'ps.tCus' },
  { key: 'roe', label: 'ps.tRoe' },
  { key: 'ctry', label: 'ps.tCtry' },
  { key: 'margin', label: 'ps.tMargin' },
];
const MAIN_TABS: readonly Tab[] = ['lcc', 'hub', 'tariff', 'cus'];
/** Nút chủ chốt của đầu trang theo tab (board `addK`). */
const ADD_LABEL: Record<Tab, string> = {
  lcc: 'ps.aLcc',
  hub: 'ps.aHub',
  tariff: 'ps.aTariff',
  cus: 'ps.aCus',
  roe: 'ps.aRoe',
  ctry: 'ps.aCtry',
  margin: 'ps.aMargin',
};
/** Tab có ngăn kéo thêm (board `dwDef`); roe / margin không có. */
const ADD_FORM: Partial<Record<Tab, SetupForm>> = { lcc: 'lcc', hub: 'hub', tariff: 'tariff', cus: 'cus', ctry: 'ctry' };
const CARRIER_CHIPS = ['MSC', 'YML', 'ONE', 'COS'] as const;
const num = (n: number, cur?: string) => (cur === 'VND' ? n.toLocaleString('vi-VN') : usd(n));

function Panel({ q, onQ, ph, chips, tools, note, children }: { q: string; ph?: string; onQ: (v: string) => void; chips?: ReactNode; tools?: ReactNode; note?: string; children: ReactNode }) {
  const t = useT();
  return (
    <section className={`${s.card} ${s.flush}`}>
      <div className={s.bar}>
        <div className={s.search}>
          <Input aria-label={t('ps.find')} placeholder={ph ?? t('ps.find')} icon={<IconSearch size={16} aria-hidden="true" />} value={q} onChange={(e) => onQ(e.target.value)} />
        </div>
        {chips}
        {tools && <div className={s.tools}>{tools}</div>}
      </div>
      {note && (
        <div className={s.note} role="note">
          <IconLock size={16} aria-hidden="true" />
          {note}
        </div>
      )}
      {children}
    </section>
  );
}

function Chips<K extends string>({ value, onChange, items, label }: { value: K; onChange: (k: K) => void; items: readonly { key: K; label: string }[]; label: string }) {
  return (
    <div className={s.chips} role="group" aria-label={label}>
      {items.map((i) => (
        <button key={i.key} type="button" className={s.chipBtn} aria-pressed={value === i.key} onClick={() => onChange(i.key)}>
          {i.label}
        </button>
      ))}
    </div>
  );
}

function LocalChargeTab({ money, onEdit, onPaste, onHist }: { money: boolean; onEdit: () => void; onPaste: () => void; onHist: () => void }) {
  const t = useT();
  const rows = (useList('localCharge').data?.items ?? []) as LocalCharge[];
  const buys = useList('localChargeBuy');
  const buyMap = useMemo(() => new Map((buys.data?.items ?? []).map((b) => [b.id, b])), [buys.data]);
  const [q, setQ] = useState('');
  const [f, setF] = useState<'all' | 'POL' | 'POD' | (typeof CARRIER_CHIPS)[number]>('all');
  const toast = useToast();
  const pending = () => toast({ msg: t('ps.noWrite'), tone: 'err' });
  const shown = rows.filter((r) => (f === 'all' || r.side === f || r.carrier === f) && matchRow([r.code, r.carrier, r.charge, r.name], q));
  const cols: Column<LocalCharge>[] = [
    { key: 'code', header: 'LocalChargeCode', width: 264, cell: (r) => <b>{r.code}</b> },
    { key: 'car', header: t('ps.carrier'), width: 70, cell: (r) => <span title={findCarrier(r.carrier)?.name ?? r.carrier}>{r.carrier}</span> },
    { key: 'side', header: t('ps.side'), width: 66, minor: true, cell: (r) => <b className={r.side === 'POD' ? s.link : undefined}>{r.side}</b> },
    { key: 'ch', header: t('ps.code'), width: 69, cell: (r) => <b>{r.charge}</b> },
    { key: 'name', header: t('ps.name'), width: 305, minor: true, cell: (r) => r.name },
    { key: 'unit', header: t('ps.unitTerm'), width: 146, minor: true, cell: (r) => `${r.unit} · ${r.term}` },
    ...(money
      ? ([
          { key: 'p20', header: '20′', width: 80, align: 'right', cell: (r) => (buyMap.get(r.id) ? num(buyMap.get(r.id)!.p20, r.cur) : '–') },
          { key: 'p40', header: '40′', width: 80, align: 'right', cell: (r) => (buyMap.get(r.id) ? num(buyMap.get(r.id)!.p40, r.cur) : '–') },
        ] as Column<LocalCharge>[])
      : []),
    { key: 'kind', header: t('ps.type'), width: 74, minor: true, cell: (r) => r.kind },
    { key: 'from', header: t('ps.valid'), width: 102, minor: true, cell: (r) => t('ps.fromV', { v: r.from }) },
    {
      key: 'act',
      header: t('ps.actions'),
      width: 144,
      align: 'right',
      cell: (r) => (
        <span className={s.acts}>
          <Button size="sm" aria-label={t('ps.edit')} title={t('ps.edit')} icon={<IconPencil size={16} aria-hidden="true" />} onClick={onEdit} />
          <Button size="sm" className={s.del} aria-label={t('ps.delAria', { v: r.charge })} title={t('ps.del')} icon={<IconTrash size={16} aria-hidden="true" />} onClick={pending} />
        </span>
      ),
    },
  ];
  return (
    <Panel q={q} onQ={setQ} ph={t('ps.lccPh')} note={money ? undefined : t('ps.buyHidden')} chips={<Chips label={t('ps.sideCar')} value={f} onChange={setF} items={[{ key: 'all', label: t('ps.all') }, { key: 'POL', label: 'POL' }, { key: 'POD', label: 'POD' }, ...CARRIER_CHIPS.map((k) => ({ key: k, label: k }))]} />} tools={<><Button icon={<IconClipboard size={16} aria-hidden="true" />} onClick={onPaste}>{t('ps.paste')}</Button><Button icon={<IconHistory size={16} aria-hidden="true" />} onClick={onHist}>{t('ps.history')}</Button></>}>
      <Table label={t('ps.tLcc')} columns={cols} rows={shown} rowKey={(r) => r.id} />
    </Panel>
  );
}

function HubTab() {
  const t = useT();
  const rows = (useList('transshipHub').data?.items ?? []) as TransshipHub[];
  const [q, setQ] = useState('');
  const shown = rows.filter((r) => matchRow([r.carrier, r.pol, r.pod, r.hub], q));
  const cols: Column<TransshipHub>[] = [
    { key: 'car', header: t('ps.carrier'), width: 90, cell: (r) => <b title={findCarrier(r.carrier)?.name ?? r.carrier}>{r.carrier}</b> },
    { key: 'pol', header: 'POL', width: 100, cell: (r) => r.pol },
    { key: 'pod', header: t('ps.podApply'), cell: (r) => r.pod },
    { key: 'hub', header: t('ps.tHub'), width: 140, cell: (r) => r.hub },
    { key: 'tt', header: t('ps.transit2'), width: 130, align: 'right', minor: true, cell: (r) => t('ps.days', { n: r.transit }) },
    { key: 'st', header: t('ps.status'), width: 110, cell: (r) => <Chip tone={r.active ? 'ok' : 'neutral'}>{r.active ? t('ps.inUse') : t('ps.paused')}</Chip> },
  ];
  return (
    <Panel q={q} onQ={setQ}>
      <Table label={t('ps.tHub')} columns={cols} rows={shown} rowKey={(r) => r.id} />
    </Panel>
  );
}

function TariffTab() {
  const t = useT();
  const rows = (useList('tariffService').data?.items ?? []) as TariffService[];
  const [q, setQ] = useState('');
  const shown = rows.filter((r) => matchRow([r.id, r.nameVn, r.nameEn, r.group], q));
  const cols: Column<TariffService>[] = [
    { key: 'id', header: 'Mã', width: 80, cell: (r) => <b>{r.id}</b> },
    { key: 'name', header: t('ps.nameVnEn'), cell: (r) => (<>{r.nameVn}<div className={s.sub}>{r.nameEn}</div></>) },
    { key: 'grp', header: t('ps.group'), width: 130, minor: true, cell: (r) => r.group },
    { key: 'unit', header: t('ps.unit'), width: 80, minor: true, cell: (r) => r.unit },
    { key: 'p20', header: '20′', width: 80, align: 'right', cell: (r) => num(r.p20, r.cur) },
    { key: 'p40', header: '40′', width: 80, align: 'right', cell: (r) => num(r.p40, r.cur) },
    { key: 'def', header: t('ps.defaultJob'), width: 130, minor: true, cell: (r) => (r.byDefault ? <Chip tone="info">{t('ps.default')}</Chip> : '–') },
    { key: 'cus', header: t('ps.cusPrice'), width: 130, align: 'right', minor: true, cell: (r) => r.customers },
  ];
  return (
    <Panel q={q} onQ={setQ}>
      <Table label={t('ps.tTariff')} columns={cols} rows={shown} rowKey={(r) => r.id} />
    </Panel>
  );
}

function CustomerTab() {
  const t = useT();
  const rows = (useList('customerSurcharge').data?.items ?? []) as CustomerSurcharge[];
  const [q, setQ] = useState('');
  const shown = rows.filter((r) => matchRow([r.customer, r.code, r.content, r.kind], q));
  const cols: Column<CustomerSurcharge>[] = [
    { key: 'cus', header: t('ps.customer'), width: 130, cell: (r) => <b>{r.customer}</b> },
    { key: 'code', header: 'Mã', width: 80, cell: (r) => r.code },
    { key: 'content', header: t('ps.content'), cell: (r) => r.content },
    { key: 'kind', header: t('ps.type'), width: 110, minor: true, cell: (r) => <Chip tone={r.kind === 'Điều khoản' ? 'neutral' : 'info'}>{r.kind}</Chip> },
    { key: 'p20', header: '20′', width: 80, align: 'right', cell: (r) => (r.cur ? num(r.p20, r.cur) : '–') },
    { key: 'p40', header: '40′', width: 80, align: 'right', cell: (r) => (r.cur ? num(r.p40, r.cur) : '–') },
    { key: 'src', header: t('ps.source'), minor: true, cell: (r) => r.source },
  ];
  return (
    <Panel q={q} onQ={setQ}>
      <Table label={t('ps.tCus')} columns={cols} rows={shown} rowKey={(r) => r.id} />
    </Panel>
  );
}

function RoeTab() {
  const t = useT();
  const rows = (useList('roe').data?.items ?? []) as RoeRow[];
  const [q, setQ] = useState('');
  const [fx, setFx] = useState('');
  const shown = rows.filter((r) => matchRow([r.day, r.code, r.cur], q));
  const ans = fx.trim() ? convertMoney(fx, rows) : null;
  const latest = latestRoe(rows, 'USD');
  const cols: Column<RoeRow>[] = [
    { key: 'day', header: t('ps.day'), width: 100, cell: (r) => <b>{r.day}</b> },
    { key: 'code', header: 'CodeROE', width: 120, minor: true, cell: (r) => r.code },
    { key: 'cur', header: t('ps.currency'), width: 90, cell: (r) => r.cur },
    { key: 'sell', header: t('ps.sell'), width: 110, align: 'right', cell: (r) => r.sell.toLocaleString('vi-VN') },
    { key: 'buy', header: t('ps.buyTransfer'), width: 130, align: 'right', cell: (r) => r.buy.toLocaleString('vi-VN') },
    { key: 'src', header: t('ps.source'), minor: true, cell: () => 'VCB' },
  ];
  return (
    <>
      <Panel q={q} onQ={setQ}>
        <Table label={t('ps.tRoe')} columns={cols} rows={shown} rowKey={(r) => r.id} />
      </Panel>
      <section className={s.card} aria-label={t('ps.convert')}>
        <h2>{t('ps.convert')}</h2>
        <Input label={t('ps.askHana')} placeholder={t('ps.askPh')} value={fx} onChange={(e) => setFx(e.target.value)} />
        <div className={s.answer} aria-live="polite">{ans ? `${ans.from} = ${ans.to}` : fx.trim() ? t('ps.noUnderstand') : ''}</div>
        {latest && <div className={s.sub}>{t('ps.perVcb', { v: latest.day })}</div>}
      </section>
    </>
  );
}

function CountryTab() {
  const t = useT();
  const rows = (useList('country').data?.items ?? []) as Country[];
  const [q, setQ] = useState('');
  const shown = rows.filter((r) => matchRow([r.id, r.name, r.region, r.continent, r.ports], q));
  const cols: Column<Country>[] = [
    { key: 'id', header: 'Mã', width: 70, cell: (r) => <b>{r.id}</b> },
    { key: 'name', header: t('ps.country'), width: 160, cell: (r) => r.name },
    { key: 'reg', header: t('ps.region'), width: 160, minor: true, cell: (r) => r.region },
    { key: 'con', header: t('ps.continent'), width: 100, minor: true, cell: (r) => r.continent },
    { key: 'ports', header: t('ps.mainPorts'), minor: true, cell: (r) => r.ports },
    { key: 'ams', header: 'AMS', width: 80, cell: (r) => (r.ams ? <Chip tone="info">AMS</Chip> : '–') },
  ];
  return (
    <Panel q={q} onQ={setQ}>
      <Table label={t('ps.tCtry')} columns={cols} rows={shown} rowKey={(r) => r.id} />
    </Panel>
  );
}

interface MarginCtl {
  /** đề nghị tạo trong phiên (chưa ghi DB) */
  extra: MarginApproval[];
  /** id đề nghị đã duyệt / từ chối trong phiên */
  done: string[];
  onEdit: (obj: string, a: string, b: string) => void;
  onDecide: (a: MarginApproval, ok: boolean) => void;
}

function MarginTab({ ctl }: { ctl: MarginCtl }) {
  const t = useT();
  const rules = (useList('marginRule').data?.items ?? []) as MarginRule[];
  const apprAll = [...ctl.extra, ...((useList('marginApproval').data?.items ?? []) as MarginApproval[])];
  const appr = apprAll.filter((a) => !ctl.done.includes(a.id));
  const waiting = (r: MarginRule) => appr.some((a) => a.obj === `${r.scope} · ${r.obj}`) || (r.pending && !apprAll.some((a) => ctl.done.includes(a.id) && a.obj.endsWith(r.obj)));
  const [q, setQ] = useState('');
  const [f, setF] = useState<'all' | MarginRule['scope']>('all');
  const shown = rules.filter((r) => (f === 'all' || r.scope === f) && matchRow([r.scope, r.obj, r.sub], q));
  const cols: Column<MarginRule>[] = [
    { key: 'scope', header: t('ps.tier'), width: 100, cell: (r) => <Chip tone={r.scope === 'Chuẩn' ? 'neutral' : r.scope === 'Khách' ? 'warn' : 'info'}>{r.scope}</Chip> },
    { key: 'obj', header: t('ps.object'), cell: (r) => (<><b>{r.obj}</b><div className={s.sub}>{r.sub}</div></>) },
    { key: 'm20', header: "Margin 20'", width: 110, align: 'right', cell: (r) => usd(r.m20) },
    { key: 'm40', header: "Margin 40'", width: 110, align: 'right', cell: (r) => usd(r.m40) },
    { key: 'floor', header: t('ps.floor'), width: 90, align: 'right', minor: true, cell: (r) => usd(r.floor) },
    { key: 'from', header: t('ps.validFrom'), width: 150, minor: true, cell: (r) => (<>{r.from}<div className={s.sub}>{t('ps.editedBy', { v: r.by })}</div></>) },
    { key: 'st', header: t('ps.status'), width: 130, cell: (r) => <Chip tone={waiting(r) ? 'warn' : 'ok'}>{waiting(r) ? t('ps.pending') : t('ps.applied')}</Chip> },
    { key: 'act', header: t('ps.actions'), width: 130, align: 'right', cell: (r) => <Button variant="ghost" disabled={waiting(r)} title={waiting(r) ? t('ps.mHasPend') : t('ps.mEditTip')} onClick={() => ctl.onEdit(`${r.scope} · ${r.obj}`, String(r.m20), String(r.m40))}>{waiting(r) ? t('ps.mWaiting') : t('ps.mEdit')}</Button> },
  ];
  return (
    <div className={s.split}>
      <Panel q={q} onQ={setQ} chips={<Chips label={t('ps.tier')} value={f} onChange={setF} items={[{ key: 'all', label: t('ps.all') }, ...MARGIN_ORDER.map((k) => ({ key: k, label: k }))]} />}>
        <div className={s.order} title={t('ps.orderTip')}>
          <b>{t('ps.order')}</b>
          {MARGIN_ORDER.map((k, i) => (
            <span key={k}>
              {MARGIN_ORDER_LABEL[k]}
              {i < MARGIN_ORDER.length - 1 ? ' ›' : ''}
            </span>
          ))}
        </div>
        <Table label={t('ps.tMargin')} columns={cols} rows={shown} rowKey={(r) => r.id} />
      </Panel>
      <aside className={s.card} aria-label={t('ps.pendingChanges')}>
        <h2>{t('ps.fourEyes')} <span className={s.n}>{appr.length}</span></h2>
        <div className={s.sub}>{t('ps.fourEyesSub')}</div>
        {appr.map((a) => (
          <div key={a.id} className={s.appr}>
            <b>{a.obj}</b>
            <div>{a.change}</div>
            <div className={s.sub}>{a.who} · {a.when}</div>
            <div className={s.sub}>{a.why}</div>
            <div className={s.apprAct}>
              <Button variant="primary" icon={<IconCheck size={16} aria-hidden="true" />} onClick={() => ctl.onDecide(a, true)}>{t('ps.approve')}</Button>
              <Button icon={<IconX size={16} aria-hidden="true" />} onClick={() => ctl.onDecide(a, false)}>{t('ps.reject')}</Button>
            </div>
          </div>
        ))}
        {appr.length === 0 && <p className={s.sub}>{t('ps.noRequests')}</p>}
      </aside>
    </div>
  );
}

export function PriceSetupScreen() {
  const t = useT();
  const access = useP2PAccess();
  const probe = useList('tariffService');
  const [tab, setTab] = useState<Tab>('lcc');
  const [more, setMore] = useState(false);
  const toast = useToast();
  const [dw, setDw] = useState<SetupDrawerState | null>(null);
  const [hist, setHist] = useState(false);
  const [mm, setMm] = useState<{ obj: string; a: string; b: string; why: string; tried: boolean } | null>(null);
  const [extra, setExtra] = useState<MarginApproval[]>([]);
  const [done, setDone] = useState<string[]>([]);
  const inMore = !MAIN_TABS.includes(tab);

  let state: ScreenState = 'ready';
  if (access.force) state = access.force;
  else if (probe.isPending) state = 'loading';
  else if (probe.error) state = probe.error instanceof DataError && probe.error.code === 'noperm' ? 'noperm' : 'error';

  const body =
    tab === 'lcc' ? <LocalChargeTab money={access.money} onEdit={() => setDw({ kind: 'edit', form: 'lcc' })} onPaste={() => setDw({ kind: 'paste' })} onHist={() => setHist(true)} /> : tab === 'hub' ? <HubTab /> : tab === 'tariff' ? <TariffTab /> : tab === 'cus' ? <CustomerTab /> : tab === 'roe' ? <RoeTab /> : tab === 'ctry' ? <CountryTab /> : access.money ? (
      <MarginTab ctl={{ extra, done, onEdit: (obj, a, b) => setMm({ obj, a, b, why: '', tried: false }), onDecide: (a, ok) => { setDone((d) => [...d, a.id]); toast({ msg: t(ok ? 'ps.mApproved' : 'ps.mRejected', { v: a.obj }) }); } }} />
    ) : (
      <StateView state="noperm" title={t('ps.marginNoperm')} desc={t('ps.marginNopermD')} />
    );

  return (
    <>
      <Page kind="table">
      <PageHeader
        title={t('ps.title')}
        tabs={
          <div className={s.seg} role="tablist" aria-label={t('ps.section')}>
            {TABS.filter((x) => MAIN_TABS.includes(x.key)).map((x) => (
              <button key={x.key} type="button" role="tab" className={s.segBtn} aria-selected={tab === x.key} onClick={() => { setTab(x.key); setMore(false); }}>
                {t(x.label)}
              </button>
            ))}
            <span className={s.moreWrap}>
              <button type="button" className={s.segBtn} aria-haspopup="menu" aria-expanded={more} aria-pressed={inMore} onClick={() => setMore(!more)}>
                {inMore ? t(TABS.find((x) => x.key === tab)?.label ?? 'ps.more') : t('ps.more')}
                <IconChevronDown size={14} aria-hidden="true" />
              </button>
              {more && (
                <div className={s.menu} role="menu" aria-label={t('ps.moreSections')}>
                  {TABS.filter((x) => !MAIN_TABS.includes(x.key)).map((x) => (
                    <button key={x.key} type="button" role="menuitemradio" aria-checked={tab === x.key} className={s.menuItem} onClick={() => { setTab(x.key); setMore(false); }}>
                      {t(x.label)}
                    </button>
                  ))}
                </div>
              )}
            </span>
          </div>
        }
        actions={
          <>
            <HeaderTool label={t('ps.moreActions')} icon={IconDots} />
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={() => { const form = ADD_FORM[tab]; if (tab === 'margin') setMm({ obj: '', a: '', b: '', why: '', tried: false }); else if (form) setDw({ kind: 'add', form }); else toast({ msg: t('ps.noWrite'), tone: 'err' }); }}>{t(ADD_LABEL[tab])}</Button>
          </>
        }
      />
      <div className={s.page}>
        <StateView
          state={state}
          title={state === 'noperm' ? t('ps.nopermT') : state === 'error' ? t('ps.errT') : undefined}
          desc={state === 'noperm' ? t('ps.nopermD') : state === 'error' ? t('ps.errD') : undefined}
          action={state === 'error' ? <Button onClick={() => void probe.refetch()}>{t('ps.retry')}</Button> : undefined}
        >
          {body}
        </StateView>
      </div>
      </Page>
      <SetupDrawer state={dw} money={access.money} onClose={() => setDw(null)} />
      <Modal
        open={!!mm}
        onOpenChange={(o) => !o && setMm(null)}
        title={t('ps.mTitle')}
        description={mm ? (mm.obj ? `${mm.obj}${mm.a ? ' · ' + t('ps.mNow', { a: mm.a, b: mm.b }) : ''}` : t('ps.mNewTier')) : undefined}
        footer={
          <div className={s.mFoot}>
            <Button variant="ghost" icon={<IconX size={16} aria-hidden="true" />} onClick={() => setMm(null)}>{t('ps.cancel')}</Button>
            <Button
              variant="primary"
              icon={<IconSend size={16} aria-hidden="true" />}
              onClick={() => {
                if (!mm) return;
                if (!mm.why.trim()) { setMm({ ...mm, tried: true }); return; }
                setExtra((e) => [{ id: `n${Date.now()}`, obj: mm.obj || t('ps.mNewTier'), change: `${mm.a} / ${mm.b} USD (20′ / 40′)`, who: 'Pricing · TRANG', when: formatDate(new Date()), why: mm.why }, ...e]);
                setMm(null);
                toast({ msg: t('ps.mSent') });
              }}
            >
              {t('ps.mSend')}
            </Button>
          </div>
        }
      >
        {mm && (
          <div className={s.mGrid}>
            <Input label="Margin 20′ (USD)" inputMode="decimal" value={mm.a} onChange={(e) => setMm({ ...mm, a: e.target.value })} />
            <Input label="Margin 40′ (USD)" inputMode="decimal" value={mm.b} onChange={(e) => setMm({ ...mm, b: e.target.value })} />
            <Input label={t('ps.validFrom')} value="01/Nov/2026" readOnly />
            <Input label={t('ps.mApprover')} value="[SAL-MGR] DAVID" readOnly />
            <div className={s.mWide}>
              <Input label={t('ps.mWhy')} placeholder={t('ps.mWhyPh')} value={mm.why} error={mm.tried && !mm.why.trim() ? t('ps.mWhyNeed') : undefined} onChange={(e) => setMm({ ...mm, why: e.target.value })} />
            </div>
          </div>
        )}
      </Modal>
      <SetupHistory open={hist} onClose={() => setHist(false)} />
    </>
  );
}
